import { logger } from '@inboundx/shared/platform';

import { supabase } from '@/lib/supabase';

// The canonical<->alias mapping is effectively static within a session, so cache
// each resolved group to avoid a domains round-trip on every analytics query.
const aliasGroupCache = new Map<string, string[]>();

/**
 * Resolve a domain to its full alias group: the canonical domain plus every
 * domain whose `canonical_domain` points at it.
 *
 * The backoffice picker only exposes canonical domains, but this resolves an
 * alias input to its full canonical group too (matching the SQL
 * `domain_alias_group()` helper), so a bookmarked alias URL or a picker race
 * still returns `[canonical, ...aliases]` rather than a truncated `[alias]`
 * (IX-3816). Used to roll up analytics across alias domains — swap
 * `.eq('site_domain', d)` for `.in('site_domain', await fetchDomainAliasGroup(d))`.
 *
 * Falls back to `[domain]` on error or when the domain has no aliases, so the
 * `.in()` filter always behaves at least like the previous `.eq()`.
 */
export async function fetchDomainAliasGroup(domain: string): Promise<string[]> {
    if (!domain) {
        return [];
    }

    const cached = aliasGroupCache.get(domain);
    if (cached) {
        return cached;
    }

    const { data, error } = await supabase
        .from('domains')
        .select('domain, canonical_domain')
        .or(`domain.eq.${domain},canonical_domain.eq.${domain}`);

    if (error) {
        logger.error('fetchDomainAliasGroup failed; falling back to single domain', {
            domain,
            error,
        });
        return [domain];
    }

    const rows = data ?? [];

    // If `domain` is an alias, its own row carries the canonical; the first query
    // then matched only that alias row (nothing has `canonical_domain = alias`),
    // so re-query off the canonical to pull the full group.
    const canonical = rows.find((row) => row.domain === domain)?.canonical_domain ?? domain;
    let groupRows: { domain: string }[] = rows;
    if (canonical !== domain) {
        const { data: canonicalData, error: canonicalError } = await supabase
            .from('domains')
            .select('domain')
            .or(`domain.eq.${canonical},canonical_domain.eq.${canonical}`);

        if (canonicalError) {
            logger.error('fetchDomainAliasGroup canonical lookup failed; falling back', {
                domain,
                canonical,
                error: canonicalError,
            });
            return [domain];
        }

        groupRows = canonicalData ?? [];
    }

    const group = groupRows.map((row) => row.domain).filter((d): d is string => Boolean(d));
    const result = group.length > 0 ? group : [domain];
    aliasGroupCache.set(domain, result);
    return result;
}
